import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from '../utils/axios';

const InviteAuditTeam = () => {
  const navigate = useNavigate();
  const { ceoId } = useParams();
  const [teamMembers, setTeamMembers] = useState([
    { name: '', email: '' },
  ]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ceoInfo, setCeoInfo] = useState(null);
  const [loadingCeoInfo, setLoadingCeoInfo] = useState(true);

  useEffect(() => {
    const fetchCeoInfo = async () => {
      try {
        const response = await axios.get(`/api/admin/ceo-info/${ceoId}`);
        setCeoInfo(response.data);
      } catch (err) {
        setError('Could not fetch CEO information');
      } finally {
        setLoadingCeoInfo(false);
      }
    };

    if (ceoId) {
      fetchCeoInfo();
    }
  }, [ceoId]);

  const handleAddMember = () => {
    if (teamMembers.length < 8) {
      setTeamMembers([...teamMembers, { name: '', email: '' }]);
    }
  };

  const handleRemoveMember = (index) => {
    const newMembers = teamMembers.filter((_, i) => i !== index);
    setTeamMembers(newMembers);
  };

  const handleChange = (index, field, value) => {
    const newMembers = [...teamMembers];
    newMembers[index][field] = value;
    setTeamMembers(newMembers);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!ceoId) {
      setError('No CEO ID found. Please return to the welcome page.');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/auth/invite-audit-team',
        { ceoId, teamMembers },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      navigate('/thank-you');
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong while sending the invitations');
    } finally {
      setLoading(false);
    }
  };

  if (loadingCeoInfo) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!ceoInfo) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">
            Could not fetch CEO information. Please check if the link is correct.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Invite Your Leadership Team for the Audit Scan
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            gutterBottom
            align="center"
            sx={{ mb: 4 }}
          >
            Add up to 8 team members who will take the audit scan anonymously. Their
            answers will be compared with yours.
          </Typography>

          <form onSubmit={handleSubmit}>
            <List>
              {teamMembers.map((member, index) => (
                <ListItem key={index}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Name (optional)"
                        value={member.name}
                        onChange={(e) =>
                          handleChange(index, 'name', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        required
                        fullWidth
                        label="Email"
                        type="email"
                        value={member.email}
                        onChange={(e) =>
                          handleChange(index, 'email', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveMember(index)}
                        disabled={teamMembers.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </ListItem>
              ))}
            </List>

            {teamMembers.length < 8 && (
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddMember}
                sx={{ mb: 3 }}
              >
                Add Team Member
              </Button>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Invitations'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default InviteAuditTeam; 